/**
 * Sharing a flow by URL, with no server involved.
 *
 * The whole payload lives in the URL fragment, which browsers never send to the
 * origin — so "share" here really does mean "hand someone a string", not
 * "upload". Nothing in this module performs a network request.
 *
 * The payload is deflated when `CompressionStream` is available and left as
 * plain UTF-8 when it is not; a one-character prefix records which, so decoding
 * never has to guess.
 */
import type { FlowDirection } from '../graph/model/types';

/** Beyond this, browsers and chat apps start truncating links. */
export const MAX_SHARE_URL_LENGTH = 8000;

const PREFIX_DEFLATED = 'd';
const PREFIX_PLAIN = 'p';

export interface ShareState {
  readonly source: string;
  readonly name?: string;
  readonly direction?: FlowDirection;
  readonly functionDisplay?: 'expanded' | 'collapsed';
}

/* ------------------------------------------------------------------ *
 * base64url
 * ------------------------------------------------------------------ */

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(value: string): Uint8Array | null {
  try {
    const padded = value.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

async function deflate(bytes: Uint8Array): Promise<Uint8Array | null> {
  if (typeof CompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([bytes as BlobPart])
      .stream()
      .pipeThrough(new CompressionStream('deflate-raw'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

async function inflate(bytes: Uint8Array): Promise<Uint8Array | null> {
  if (typeof DecompressionStream === 'undefined') return null;
  try {
    const stream = new Blob([bytes as BlobPart])
      .stream()
      .pipeThrough(new DecompressionStream('deflate-raw'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * Encoding
 * ------------------------------------------------------------------ */

export async function encodeShareState(state: ShareState): Promise<string> {
  const json = JSON.stringify(compact(state));
  const raw = new TextEncoder().encode(json);
  const deflated = await deflate(raw);
  return deflated
    ? PREFIX_DEFLATED + bytesToBase64Url(deflated)
    : PREFIX_PLAIN + bytesToBase64Url(raw);
}

export async function decodeShareState(encoded: string): Promise<ShareState | null> {
  const trimmed = encoded.replace(/^#/, '').replace(/^flow=/, '');
  if (trimmed.length < 2) return null;

  const prefix = trimmed[0];
  const bytes = base64UrlToBytes(trimmed.slice(1));
  if (!bytes) return null;

  let raw: Uint8Array | null = bytes;
  if (prefix === PREFIX_DEFLATED) {
    raw = await inflate(bytes);
  } else if (prefix !== PREFIX_PLAIN) {
    return null;
  }
  if (!raw) return null;

  try {
    return validate(JSON.parse(new TextDecoder().decode(raw)) as unknown);
  } catch {
    return null;
  }
}

export interface ShareUrl {
  readonly url: string;
  readonly tooLong: boolean;
}

export async function buildShareUrl(base: string, state: ShareState): Promise<ShareUrl> {
  const encoded = await encodeShareState(state);
  const url = `${base.split('#')[0] ?? base}#flow=${encoded}`;
  return { url, tooLong: url.length > MAX_SHARE_URL_LENGTH };
}

export async function readShareStateFromLocation(location: {
  hash: string;
}): Promise<ShareState | null> {
  const hash = location.hash ?? '';
  if (!hash.startsWith('#flow=')) return null;
  return decodeShareState(hash.slice('#flow='.length));
}

/* ------------------------------------------------------------------ *
 * Validation
 * ------------------------------------------------------------------ */

function compact(state: ShareState): Record<string, unknown> {
  const out: Record<string, unknown> = { source: state.source };
  if (state.name) out.name = state.name;
  if (state.direction) out.direction = state.direction;
  if (state.functionDisplay) out.functionDisplay = state.functionDisplay;
  return out;
}

/** Same discipline as the JSON importer: a shared link is untrusted input. */
function validate(value: unknown): ShareState | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;

  if (typeof record.source !== 'string') return null;
  if (record.source.length > 2_000_000) return null;

  const state: {
    source: string;
    name?: string;
    direction?: FlowDirection;
    functionDisplay?: 'expanded' | 'collapsed';
  } = { source: record.source };

  if (typeof record.name === 'string') state.name = record.name.slice(0, 200);
  if (record.direction === 'top-to-bottom' || record.direction === 'left-to-right') {
    state.direction = record.direction;
  }
  if (record.functionDisplay === 'expanded' || record.functionDisplay === 'collapsed') {
    state.functionDisplay = record.functionDisplay;
  }
  return state;
}
